import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Loader2, Save, X } from "lucide-react";
import {
  SafetyTemplate, SafetyInstructionType, SafetyTargetGroup,
  TYPE_LABEL, TARGET_GROUP_LABEL,
} from "./types";

// Gesetzliche Vorgaben für Sicherheitsunterweisungen (DE)
const LEGAL_BASIS_PRESETS: Record<SafetyInstructionType | "common", string[]> = {
  common: [
    "ArbSchG § 12 (Unterweisung)",
    "DGUV Vorschrift 1 § 4 (Unterweisung der Versicherten)",
    "BetrSichV § 12 (Unterweisung)",
    "ArbStättV § 6 Abs. 1 (Unterweisung)",
  ],
  initial: [
    "ArbSchG § 12 Abs. 1 (Erstunterweisung vor Aufnahme der Tätigkeit)",
    "JArbSchG § 29 (Unterweisung Jugendlicher)",
    "GefStoffV § 14 (Unterweisung bei Gefahrstoffen)",
  ],
  annual: [
    "DGUV Vorschrift 1 § 4 Abs. 1 (mindestens jährlich)",
    "GefStoffV § 14 Abs. 2 (jährliche Unterweisung)",
    "BioStoffV § 14 (jährliche Unterweisung)",
  ],
  incident: [
    "ArbSchG § 12 Abs. 1 Satz 3 (bei Veränderungen / Anlass)",
    "DGUV Vorschrift 1 § 4 (bei Unfällen / neuen Gefährdungen)",
  ],
  site: [
    "BaustellV § 4 (Koordination & Unterweisung auf Baustellen)",
    "DGUV Vorschrift 38 (Bauarbeiten)",
    "RAB 30 (Geeigneter Koordinator)",
  ],
  special_critical_area: [
    "GefStoffV § 14 (Tätigkeiten mit Gefahrstoffen)",
    "BioStoffV § 14 (Biologische Arbeitsstoffe)",
    "DGUV Vorschrift 3 (Elektrische Anlagen)",
    "TRBS 1201 (Prüfung / kritische Bereiche)",
  ],
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: SafetyTemplate | null;
  onSaved: () => void;
}

const emptyForm = {
  title: "",
  description: "",
  instruction_type: "annual" as SafetyInstructionType,
  target_group: "all" as SafetyTargetGroup,
  legal_basis: [] as string[],
  legal_basis_custom: "",
  repeat_interval_months: 12,
  passing_score_percent: 80,
  max_attempts: 3,
  requires_followup_resolution: true,
  valid_from: "",
  valid_until: "",
  responsible_admin_id: "",
};

export const SafetyTemplateEditor = ({ open, onOpenChange, template, onSaved }: Props) => {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);

  const isLocked = template?.status === "approved" || template?.status === "active" || template?.status === "archived";

  useEffect(() => {
    if (open) {
      supabase.from("employees").select("id, name").is("archived_at", null).order("name").then(({ data }) => {
        if (data) setEmployees(data);
      });
    }
  }, [open]);

  useEffect(() => {
    if (template) {
      setForm({
        title: template.title,
        description: template.description || "",
        instruction_type: template.instruction_type,
        target_group: template.target_group,
        legal_basis: template.legal_basis || [],
        legal_basis_custom: "",
        repeat_interval_months: template.repeat_interval_months,
        passing_score_percent: template.passing_score_percent,
        max_attempts: template.max_attempts,
        requires_followup_resolution: template.requires_followup_resolution,
        valid_from: template.valid_from || "",
        valid_until: template.valid_until || "",
        responsible_admin_id: template.responsible_admin_id || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [template, open]);

  const handleSave = async () => {
    if (!user) return;
    if (!form.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        instruction_type: form.instruction_type,
        target_group: form.target_group,
        legal_basis: Array.from(new Set([
          ...form.legal_basis,
          ...form.legal_basis_custom.split("\n").map((s) => s.trim()).filter(Boolean),
        ])),
        repeat_interval_months: form.repeat_interval_months,
        passing_score_percent: form.passing_score_percent,
        max_attempts: form.max_attempts,
        requires_followup_resolution: form.requires_followup_resolution,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        responsible_admin_id: form.responsible_admin_id || null,
      };

      if (template) {
        const { error } = await supabase
          .from("safety_instruction_templates")
          .update(payload)
          .eq("id", template.id);
        if (error) throw error;
        toast.success("Vorlage aktualisiert");
      } else {
        const { error } = await supabase
          .from("safety_instruction_templates")
          .insert({ ...payload, created_by: user.id, status: "draft" });
        if (error) throw error;
        toast.success("Vorlage erstellt");
      }
      onSaved();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? "Vorlage bearbeiten" : "Neue Vorlage"}</DialogTitle>
        </DialogHeader>

        {isLocked && (
          <div className="p-3 rounded bg-amber-500/10 border border-amber-500/20 text-sm">
            Diese Vorlage ist freigegeben. Inhaltliche Änderungen erfordern eine neue Version.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label>Titel *</Label>
            <Input value={form.title} disabled={isLocked} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Textarea rows={3} value={form.description} disabled={isLocked} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Unterweisungstyp</Label>
              <Select value={form.instruction_type} disabled={isLocked} onValueChange={(v) => setForm({ ...form, instruction_type: v as SafetyInstructionType })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Zielgruppe</Label>
              <Select value={form.target_group} disabled={isLocked} onValueChange={(v) => setForm({ ...form, target_group: v as SafetyTargetGroup })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TARGET_GROUP_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Rechtsgrundlagen (gesetzliche Vorgaben)</Label>
            <div className="border rounded-md p-3 space-y-3 bg-muted/30">
              {(["common", form.instruction_type] as const).map((group) => {
                const opts = LEGAL_BASIS_PRESETS[group] || [];
                if (!opts.length) return null;
                return (
                  <div key={group} className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {group === "common" ? "Allgemein" : TYPE_LABEL[form.instruction_type]}
                    </div>
                    {opts.map((opt) => {
                      const checked = form.legal_basis.includes(opt);
                      return (
                        <label key={opt} className="flex items-start gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={checked}
                            disabled={isLocked}
                            onCheckedChange={(c) => {
                              setForm({
                                ...form,
                                legal_basis: c
                                  ? [...form.legal_basis, opt]
                                  : form.legal_basis.filter((x) => x !== opt),
                              });
                            }}
                          />
                          <span>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
              {form.legal_basis.filter((b) => !Object.values(LEGAL_BASIS_PRESETS).flat().includes(b)).length > 0 && (
                <div className="space-y-1.5 pt-2 border-t">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Eigene Einträge</div>
                  {form.legal_basis
                    .filter((b) => !Object.values(LEGAL_BASIS_PRESETS).flat().includes(b))
                    .map((b) => (
                      <div key={b} className="flex items-center justify-between gap-2 text-sm">
                        <span>{b}</span>
                        {!isLocked && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => setForm({ ...form, legal_basis: form.legal_basis.filter((x) => x !== b) })}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Weitere Rechtsgrundlagen (optional, eine pro Zeile)</Label>
              <Textarea
                rows={2}
                value={form.legal_basis_custom}
                disabled={isLocked}
                placeholder="z. B. TRGS 500"
                onChange={(e) => setForm({ ...form, legal_basis_custom: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Wiederholung (Monate)</Label>
              <Input type="number" min={0} value={form.repeat_interval_months} disabled={isLocked} onChange={(e) => setForm({ ...form, repeat_interval_months: parseInt(e.target.value || "0") })} />
            </div>
            <div>
              <Label>Bestehensgrenze (%)</Label>
              <Input type="number" min={0} max={100} value={form.passing_score_percent} disabled={isLocked} onChange={(e) => setForm({ ...form, passing_score_percent: parseInt(e.target.value || "0") })} />
            </div>
            <div>
              <Label>Max. Fehlversuche</Label>
              <Input type="number" min={1} value={form.max_attempts} disabled={isLocked} onChange={(e) => setForm({ ...form, max_attempts: parseInt(e.target.value || "0") })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {([
              { key: "valid_from", label: "Gültig ab" },
              { key: "valid_until", label: "Gültig bis" },
            ] as const).map(({ key, label }) => {
              const value = form[key];
              const date = value ? parseISO(value) : undefined;
              return (
                <div key={key}>
                  <Label>{label}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP", { locale: de }) : <span>Datum wählen</span>}
                        {date && (
                          <X
                            className="ml-auto h-4 w-4 opacity-60 hover:opacity-100"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setForm({ ...form, [key]: "" }); }}
                          />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => setForm({ ...form, [key]: d ? format(d, "yyyy-MM-dd") : "" })}
                        initialFocus
                        locale={de}
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              );
            })}
          </div>
          <div>
            <Label>Verantwortliche/unterweisende Person</Label>
            <Select value={form.responsible_admin_id || "none"} onValueChange={(v) => setForm({ ...form, responsible_admin_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="Standard: Administrator" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Standard (Administrator)</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Pflicht zur Rückfrageklärung</Label>
            <Switch checked={form.requires_followup_resolution} disabled={isLocked} onCheckedChange={(c) => setForm({ ...form, requires_followup_resolution: c })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
